/**
 * Team Modules tab for managing team-scoped modules.
 * Accessible to team administrators and account administrators.
 */
import {
	BarChart,
	BarChart3,
	Bell,
	Box,
	Building,
	Calendar,
	ChartPie,
	CircleDollarSign,
	Cog,
	Database,
	Edit,
	FileText,
	Folder,
	Globe,
	Home,
	Inbox,
	LayoutDashboard,
	LineChart,
	Mail,
	Map,
	Megaphone,
	MessageSquare,
	Monitor,
	PieChart,
	Plus,
	Search,
	Settings,
	ShoppingCart,
	Target,
	Trash2,
	TrendingUp,
	User,
	Users,
	Zap,
} from "lucide-react";
import { useState } from "react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@/components/ui/table";
import { Textarea } from "@/components/ui/textarea";
import { trpc } from "@/lib/trpc";
import { useAuthStore } from "@/stores/auth";

/** Map of icon names to Lucide icon components. */
const iconMap: Record<string, typeof LayoutDashboard> = {
	BarChart,
	BarChart3,
	Bell,
	Box,
	Building,
	Calendar,
	ChartPie,
	CircleDollarSign,
	Cog,
	Database,
	FileText,
	Folder,
	Globe,
	Home,
	Inbox,
	LayoutDashboard,
	LineChart,
	Mail,
	Map,
	Megaphone,
	MessageSquare,
	Monitor,
	PieChart,
	Search,
	Settings,
	ShoppingCart,
	Target,
	TrendingUp,
	User,
	Users,
	Zap,
};

interface ModuleFormData {
	name: string;
	slug: string;
	description: string;
	icon: string;
	enabled: boolean;
	sequentialExecution: boolean;
}

const defaultFormData: ModuleFormData = {
	name: "",
	slug: "",
	description: "",
	icon: "",
	enabled: true,
	sequentialExecution: true,
};

/**
 * Team Modules management tab
 * Allows team admins to create and manage team-scoped modules
 */
export function TeamModulesTab() {
	const { activeTeam } = useAuthStore();
	const utils = trpc.useUtils();

	// Dialog states
	const [createDialogOpen, setCreateDialogOpen] = useState(false);
	const [editingModule, setEditingModule] = useState<string | null>(null);
	const [deletingModule, setDeletingModule] = useState<{
		id: string;
		name: string;
	} | null>(null);

	// Form state
	const [formData, setFormData] = useState<ModuleFormData>(defaultFormData);

	// Query for brand modules
	const modulesQuery = trpc.modules.team.list.useQuery();

	// Mutations
	const createMutation = trpc.modules.team.create.useMutation({
		onSuccess: () => {
			void utils.modules.team.list.invalidate();
			void utils.modules.list.invalidate();
			setCreateDialogOpen(false);
			resetForm();
		},
	});

	const updateMutation = trpc.modules.team.update.useMutation({
		onSuccess: () => {
			void utils.modules.team.list.invalidate();
			void utils.modules.list.invalidate();
			setEditingModule(null);
			resetForm();
		},
	});

	const deleteMutation = trpc.modules.team.delete.useMutation({
		onSuccess: () => {
			void utils.modules.team.list.invalidate();
			void utils.modules.list.invalidate();
			setDeletingModule(null);
		},
	});

	/**
	 * Reset form to default values
	 */
	const resetForm = () => {
		setFormData(defaultFormData);
	};

	/**
	 * Handle module creation
	 */
	const handleCreate = () => {
		createMutation.mutate({
			name: formData.name,
			slug: formData.slug,
			description: formData.description || null,
			icon: formData.icon || null,
			enabled: formData.enabled,
			sequentialExecution: formData.sequentialExecution,
		});
	};

	/**
	 * Handle module update
	 */
	const handleUpdate = () => {
		if (!editingModule) return;
		updateMutation.mutate({
			moduleId: editingModule,
			name: formData.name,
			slug: formData.slug,
			description: formData.description || null,
			icon: formData.icon || null,
			enabled: formData.enabled,
			sequentialExecution: formData.sequentialExecution,
		});
	};

	/**
	 * Handle module deletion
	 */
	const handleDelete = () => {
		if (!deletingModule) return;
		deleteMutation.mutate({ moduleId: deletingModule.id });
	};

	/**
	 * Open edit dialog with module data
	 * @param module - Module to edit
	 * @param module.id - Module ID
	 * @param module.name - Module name
	 * @param module.slug - Module slug
	 * @param module.description - Module description
	 * @param module.icon - Module icon
	 * @param module.enabled - Module enabled state
	 * @param module.sequentialExecution - Sequential execution flag
	 */
	const openEditDialog = (module: {
		id: string;
		name: string;
		slug: string;
		description: string | null;
		icon: string | null;
		enabled: boolean;
		sequentialExecution: boolean;
	}) => {
		setFormData({
			name: module.name,
			slug: module.slug,
			description: module.description ?? "",
			icon: module.icon ?? "",
			enabled: module.enabled,
			sequentialExecution: module.sequentialExecution,
		});
		setEditingModule(module.id);
	};

	/**
	 * Handle name change and auto-generate slug for new modules
	 * @param name - Module name
	 * @param isCreate - Whether this is a new module (affects slug generation)
	 */
	const handleNameChange = (name: string, isCreate: boolean) => {
		setFormData((prev) => ({
			...prev,
			name,
			// Only auto-generate slug for new modules
			slug: isCreate ? name.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "") : prev.slug,
		}));
	};

	if (!activeTeam) {
		return (
			<div className="p-4 text-muted-foreground">
				Please select a team to manage team modules.
			</div>
		);
	}

	return (
		<div className="space-y-4">
			{/* Header */}
			<div className="flex items-center justify-between">
				<div>
					<h2 className="text-lg font-semibold">Team Modules</h2>
					<p className="text-sm text-muted-foreground">
						Modules specific to {activeTeam.name}
					</p>
				</div>
				<Button
					onClick={() => {
						resetForm();
						setCreateDialogOpen(true);
					}}
				>
					<Plus className="mr-2 h-4 w-4" />
					Create Module
				</Button>
			</div>

			{/* Modules Table */}
			<div className="rounded-lg border">
				{modulesQuery.isLoading ? (
					<div className="p-8 text-center text-muted-foreground">Loading modules...</div>
				) : modulesQuery.error ? (
					<div className="p-8 text-center text-destructive">
						Error: {modulesQuery.error.message}
					</div>
				) : modulesQuery.data?.modules.length === 0 ? (
					<div className="p-8 text-center text-muted-foreground">
						No team modules found. Create your first module to get started.
					</div>
				) : (
					<Table>
						<TableHeader>
							<TableRow>
								<TableHead>Name</TableHead>
								<TableHead>Slug</TableHead>
								<TableHead>Status</TableHead>
								<TableHead>Sequential</TableHead>
								<TableHead className="w-[100px]">Actions</TableHead>
							</TableRow>
						</TableHeader>
						<TableBody>
							{modulesQuery.data?.modules.map((module) => {
								const Icon = iconMap[module.icon ?? ""] ?? LayoutDashboard;
								return (
									<TableRow key={module.id}>
										<TableCell className="font-medium">
											<div className="flex items-center gap-2">
												<Icon className="h-4 w-4" />
												{module.name}
											</div>
										</TableCell>
										<TableCell className="text-muted-foreground">{module.slug}</TableCell>
										<TableCell>
											<Badge variant={module.enabled ? "default" : "secondary"}>
												{module.enabled ? "Enabled" : "Disabled"}
											</Badge>
										</TableCell>
										<TableCell>
											<Badge variant={module.sequentialExecution ? "outline" : "secondary"}>
												{module.sequentialExecution ? "Yes" : "No"}
											</Badge>
										</TableCell>
										<TableCell>
											<div className="flex items-center gap-2">
												<Button
													variant="ghost"
													size="icon"
													onClick={() => {
														openEditDialog(module);
													}}
												>
													<Edit className="h-4 w-4" />
												</Button>
												<Button
													variant="ghost"
													size="icon"
													onClick={() => {
														setDeletingModule({ id: module.id, name: module.name });
													}}
												>
													<Trash2 className="h-4 w-4" />
												</Button>
											</div>
										</TableCell>
									</TableRow>
								);
							})}
						</TableBody>
					</Table>
				)}
			</div>

			{/* Create Dialog */}
			<Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
				<DialogContent>
					<DialogHeader>
						<DialogTitle>Create Team Module</DialogTitle>
						<DialogDescription>
							Create a new module specific to {activeTeam.name}.
						</DialogDescription>
					</DialogHeader>
					<form
						onSubmit={(e) => {
							e.preventDefault();
							handleCreate();
						}}
					>
						<div className="grid gap-4 py-4">
							<div className="grid gap-2">
								<Label htmlFor="name">Name</Label>
								<Input
									id="name"
									value={formData.name}
									onChange={(e) => {
										handleNameChange(e.target.value, true);
									}}
									placeholder="My Module"
								/>
							</div>
							<div className="grid gap-2">
								<Label htmlFor="slug">Slug</Label>
								<Input
									id="slug"
									value={formData.slug}
									onChange={(e) => {
										setFormData((prev) => ({ ...prev, slug: e.target.value }));
									}}
									placeholder="my-module"
								/>
							</div>
							<div className="grid gap-2">
								<Label htmlFor="description">Description</Label>
								<Textarea
									id="description"
									value={formData.description}
									onChange={(e) => {
										setFormData((prev) => ({ ...prev, description: e.target.value }));
									}}
									placeholder="Optional description..."
								/>
							</div>
							<div className="grid gap-2">
								<Label htmlFor="icon">Icon (emoji)</Label>
								<Input
									id="icon"
									value={formData.icon}
									onChange={(e) => {
										setFormData((prev) => ({ ...prev, icon: e.target.value }));
									}}
									placeholder="📦"
								/>
							</div>
							<div className="flex items-center gap-2">
								<Checkbox
									id="enabled"
									checked={formData.enabled}
									onCheckedChange={(checked: boolean) => {
										setFormData((prev) => ({ ...prev, enabled: checked }));
									}}
								/>
								<Label htmlFor="enabled">Enabled</Label>
							</div>
							<div className="flex items-center gap-2">
								<Checkbox
									id="sequential"
									checked={formData.sequentialExecution}
									onCheckedChange={(checked: boolean) => {
										setFormData((prev) => ({ ...prev, sequentialExecution: checked }));
									}}
								/>
								<Label htmlFor="sequential">Sequential Execution</Label>
							</div>
						</div>
						{createMutation.error && (
							<p className="text-sm text-destructive mb-4">{createMutation.error.message}</p>
						)}
						<DialogFooter>
							<Button
								type="button"
								variant="outline"
								onClick={() => {
									setCreateDialogOpen(false);
									resetForm();
								}}
							>
								Cancel
							</Button>
							<Button type="submit" disabled={!formData.name || !formData.slug || createMutation.isPending}>
								{createMutation.isPending ? "Creating..." : "Create Module"}
							</Button>
						</DialogFooter>
					</form>
				</DialogContent>
			</Dialog>

			{/* Edit Dialog */}
			<Dialog
				open={!!editingModule}
				onOpenChange={(open) => {
					if (!open) {
						setEditingModule(null);
						resetForm();
					}
				}}
			>
				<DialogContent>
					<DialogHeader>
						<DialogTitle>Edit Team Module</DialogTitle>
						<DialogDescription>Update the module configuration.</DialogDescription>
					</DialogHeader>
					<form
						onSubmit={(e) => {
							e.preventDefault();
							handleUpdate();
						}}
					>
						<div className="grid gap-4 py-4">
							<div className="grid gap-2">
								<Label htmlFor="edit-name">Name</Label>
								<Input
									id="edit-name"
									value={formData.name}
									onChange={(e) => {
										handleNameChange(e.target.value, false);
									}}
									placeholder="My Module"
								/>
							</div>
							<div className="grid gap-2">
								<Label htmlFor="edit-slug">Slug</Label>
								<Input
									id="edit-slug"
									value={formData.slug}
									onChange={(e) => {
										setFormData((prev) => ({ ...prev, slug: e.target.value }));
									}}
									placeholder="my-module"
								/>
							</div>
							<div className="grid gap-2">
								<Label htmlFor="edit-description">Description</Label>
								<Textarea
									id="edit-description"
									value={formData.description}
									onChange={(e) => {
										setFormData((prev) => ({ ...prev, description: e.target.value }));
									}}
									placeholder="Optional description..."
								/>
							</div>
							<div className="grid gap-2">
								<Label htmlFor="edit-icon">Icon (emoji)</Label>
								<Input
									id="edit-icon"
									value={formData.icon}
									onChange={(e) => {
										setFormData((prev) => ({ ...prev, icon: e.target.value }));
									}}
									placeholder="📦"
								/>
							</div>
							<div className="flex items-center gap-2">
								<Checkbox
									id="edit-enabled"
									checked={formData.enabled}
									onCheckedChange={(checked: boolean) => {
										setFormData((prev) => ({ ...prev, enabled: checked }));
									}}
								/>
								<Label htmlFor="edit-enabled">Enabled</Label>
							</div>
							<div className="flex items-center gap-2">
								<Checkbox
									id="edit-sequential"
									checked={formData.sequentialExecution}
									onCheckedChange={(checked: boolean) => {
										setFormData((prev) => ({ ...prev, sequentialExecution: checked }));
									}}
								/>
								<Label htmlFor="edit-sequential">Sequential Execution</Label>
							</div>
						</div>
						{updateMutation.error && (
							<p className="text-sm text-destructive mb-4">{updateMutation.error.message}</p>
						)}
						<DialogFooter>
							<Button
								type="button"
								variant="outline"
								onClick={() => {
									setEditingModule(null);
									resetForm();
								}}
							>
								Cancel
							</Button>
							<Button type="submit" disabled={!formData.name || !formData.slug || updateMutation.isPending}>
								{updateMutation.isPending ? "Saving..." : "Save Changes"}
							</Button>
						</DialogFooter>
					</form>
				</DialogContent>
			</Dialog>

			{/* Delete Confirmation Dialog */}
			<Dialog
				open={!!deletingModule}
				onOpenChange={(open) => {
					if (!open) setDeletingModule(null);
				}}
			>
				<DialogContent>
					<DialogHeader>
						<DialogTitle>Delete Module</DialogTitle>
						<DialogDescription>
							Are you sure you want to delete &quot;{deletingModule?.name}&quot;? This action cannot be undone
							and will remove all associated actions and executions.
						</DialogDescription>
					</DialogHeader>
					{deleteMutation.error && (
						<p className="text-sm text-destructive">{deleteMutation.error.message}</p>
					)}
					<DialogFooter>
						<Button
							type="button"
							variant="outline"
							onClick={() => {
								setDeletingModule(null);
							}}
						>
							Cancel
						</Button>
						<Button
							variant="destructive"
							onClick={handleDelete}
							disabled={deleteMutation.isPending}
						>
							{deleteMutation.isPending ? "Deleting..." : "Delete Module"}
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>
		</div>
	);
}
